import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import TextInputP from "../../common/inputElements/TextInputP";
import { postAuthenticatedRequest } from "../../utils/API/api.ts";
import { showToast } from "../../redux/actions/toastActions";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

export default function CardDataModal({
  cardData = {},
  onSubmit,
  onClose,
  triggerModal,
  register,
  watch,
  getValues,
  setValue,
  eventsId,
  attendeeId,
  reset,
  edit,
}) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inputFields = [
    { id: "name", label: "Name" },
    { id: "companyName", label: "Company Name" },
    { id: "role", label: "Role" },
    { id: "email", label: "Email", type: "email" },
    { id: "mobile", label: "Mobile" },
    { id: "website", label: "Website" },
    { id: "address", label: "Address" },
  ];

  const imageData = watch("imageData") || watch("imageUrl");

  const handleSave = async () => {
    setLoading(true);
    const formData = getValues();

    try {
      const payload = {
        name: formData.name || "",
        role: formData.role || "",
        companyName: formData.companyName || "",
        email: formData.email || "",
        mobile: formData.mobile || "",
        website: formData.website || "",
        address: formData.address || "",
        imageData: formData.imageData || "",
        id: formData?._id || "",
      };

      const response = await postAuthenticatedRequest(
        `${
          process.env.REACT_APP_SERVER_URL_LEADS
        }/attendee/${attendeeId}/addCardLeads?eventId=${eventsId}&edit=${
          edit ? "true" : "false"
        }`,
        payload
      );

      queryClient.invalidateQueries(["cardLeads", eventsId, 1, ""], {
        exact: false,
      });

      if (response.status === 200) {
        dispatch(
          showToast({
            message: response?.data?.message || "Card lead added successfully!",
            category: "success",
          })
        );
        setValue("cardModalOpen", false);
        onClose();
        navigate(`/attendee/${eventsId}?tab=cards`);
      } else {
        dispatch(
          showToast({ message: "Failed to add card lead", category: "danger" })
        );
      }
    } catch (err) {
      console.error(err);
      dispatch(
        showToast({
          message:
            err?.data?.message || "Something went wrong while adding card lead",
          category: "danger",
        })
      );
      setValue("cardModalOpen", false);
    } finally {
      reset();
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {triggerModal && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-0 md:p-4"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <div className="bg-white w-full h-full md:h-[80%] md:w-[500px] md:rounded-2xl shadow-xl flex flex-col overflow-hidden">
              <div className="flex justify-between items-center p-4 border-b flex-shrink-0 bg-white z-10">
                <h2 className="text-[20px] font-semibold">Card Info</h2>
                <button onClick={onClose}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 18 18"
                    className="fill-current text-gray-700"
                  >
                    <path d="M0 0h18v18H0z" fill="none" />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M10.293 9l4.646-4.646a1 1 0 10-1.414-1.414L9 7.586 4.354 2.94a1 1 0 10-1.414 1.414L7.586 9l-4.646 4.646a1 1 0 001.414 1.414L9 10.414l4.646 4.646a1 1 0 001.414-1.414L10.414 9z"
                    />
                  </svg>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 pt-0 pb-5 space-y-4">
                {inputFields.map(({ id, label, type = "text" }) => (
                  <TextInputP
                    key={id}
                    id={id}
                    label={label}
                    type={type}
                    register={register}
                  />
                ))}

                {imageData && (
                  <div className="mt-4 flex justify-center ">
                    <img
                      src={imageData}
                      alt="Card Preview"
                      className="max-h-60 w-full rounded-lg border"
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-between gap-2 p-4 border-t bg-white flex-shrink-0">
                <button
                  className="w-1/2 bg-white border border-primary text-primary rounded-[10px] h-[45px] font-medium"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  className={`w-1/2 bg-primary text-white rounded-[10px] h-[45px] font-medium ${
                    loading && "cursor-not-allowed"
                  }`}
                  onClick={handleSave}
                >
                  {loading ? "Saving...." : "Save"}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
